/**
 * Medication Scan Assistant — mobile-first multi-photo prescription capture.
 *
 * Three-phase UI:
 *   1. CAPTURE  — guided per-step camera capture (5 steps, last optional).
 *                 Uses `<input type="file" capture="environment">` so the
 *                 phone opens the back camera directly. Thumbnails, retake,
 *                 delete, add another, progress indicator.
 *   2. PROCESS  — fan-out: each photo gets OCR'd in parallel via the
 *                 /api/prescriptions/ocr endpoint and barcode-scanned via
 *                 the native BarcodeDetector → ZXing fallback.
 *   3. REVIEW   — merged candidate fields with confidence badges; conflict
 *                 picker for fields where photos disagreed; one-tap
 *                 "Confirm All" returns the payload up to the form.
 *
 * Privacy posture (per spec): photos NEVER persisted. Each Blob lives in
 * component state, used in-flight for the OCR multipart upload, and is
 * GC'd as soon as the user exits the modal or saves.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  X, Camera, ArrowsClockwise, Trash, Plus, CheckCircle, Warning, WarningCircle,
  ShieldCheck, Sparkle, Spinner, ArrowRight, Barcode, Lightbulb,
} from "@phosphor-icons/react";
import {
  newSession, addPhoto, removePhoto, replacePhoto,
  setOcrResult, setBarcodeResult, mergeCandidates,
  applyUserChoice, finalConfirmed, PHOTO_STEPS,
} from "@/lib/rxScanSession";
import { decodeBarcodeDetailedFromFile } from "@/lib/barcodeScanner";
import { scanPrescriptionImage, learnPharmacyPattern, matchPharmacyPattern } from "@/lib/api";
import { showToast } from "@/lib/toast";

const ACCEPTED_MIME = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

// Field display order + labels — same as iteration_33's review modal but
// optimised for one-handed mobile review (big inputs, larger spacing).
const FIELD_ROWS = [
  { id: "medName",          label: "Medication name",      placeholder: "Ibuprofen" },
  { id: "strength",         label: "Strength / dose",      placeholder: "600 mg" },
  { id: "directions",       label: "Directions (SIG)",     placeholder: "Take by mouth three times daily with food" },
  { id: "prescriber",       label: "Prescriber",           placeholder: "Dr. Smith, VAMC Greensboro" },
  { id: "pharmacy",         label: "Pharmacy",             placeholder: "NMR/TU Atsugi" },
  { id: "rxNumber",         label: "Rx number",            placeholder: "RX9988123" },
  { id: "quantity",         label: "Quantity",             placeholder: "30 tablets" },
  { id: "lastFilled",       label: "Date filled",          placeholder: "2026-02-15", type: "date" },
  { id: "daysSupply",       label: "Days supply",          placeholder: "30",         type: "number" },
  { id: "refillsRemaining", label: "Refills remaining",    placeholder: "0",          type: "number" },
  { id: "notes",            label: "Notes / warnings",     placeholder: "Take with food. May cause drowsiness." },
];

function statusChip(status) {
  switch (status) {
    case "high":      return { label: "HIGH",           color: "#047857", bg: "rgba(16,185,129,0.10)",  border: "rgba(16,185,129,0.32)" };
    case "resolved":  return { label: "RESOLVED",       color: "#047857", bg: "rgba(16,185,129,0.10)",  border: "rgba(16,185,129,0.32)" };
    case "review":    return { label: "NEEDS REVIEW",   color: "#b45309", bg: "rgba(217,119,6,0.10)",   border: "rgba(217,119,6,0.32)"  };
    case "low":       return { label: "LOW",            color: "#b91c1c", bg: "rgba(220,38,38,0.10)",   border: "rgba(220,38,38,0.32)"  };
    case "missing":   return { label: "MISSING",        color: "#64748b", bg: "rgba(100,116,139,0.10)", border: "rgba(100,116,139,0.32)"};
    case "conflict":  return { label: "CONFLICT",       color: "#b91c1c", bg: "rgba(220,38,38,0.10)",   border: "rgba(220,38,38,0.32)"  };
    default:          return null;
  }
}

export default function MedicationScanAssistant({ onCancel, onConfirm }) {
  const [phase, setPhase] = useState("capture"); // "capture" | "process" | "review"
  const [session, setSession] = useState(newSession);
  const [stepIdx, setStepIdx] = useState(0);     // current capture step
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState("");
  const [userOverrides, setUserOverrides] = useState({});

  const step = PHOTO_STEPS[stepIdx];
  const photosForStep = session.photos.filter((p) => p.stepId === step?.id);

  // Tidy up any object URLs on unmount
  useEffect(() => {
    return () => {
      session.photos.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch { /* ignore */ } });
    };
  }, [session.photos]);

  function handlePick(e, replaceId = null) {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!ACCEPTED_MIME.includes(f.type)) {
      showToast({ variant: "error", message: "Use a JPEG, PNG, or WEBP image.", duration: 3000 });
      return;
    }
    const url = URL.createObjectURL(f);
    if (replaceId) {
      setSession((s) => replacePhoto(s, replaceId, { blob: f, url }));
    } else {
      setSession((s) => addPhoto(s, { stepId: step.id, blob: f, url }));
    }
  }

  function handleDelete(photoId) {
    const p = session.photos.find((pp) => pp.id === photoId);
    if (p?.url) { try { URL.revokeObjectURL(p.url); } catch { /* ignore */ } }
    setSession((s) => removePhoto(s, photoId));
  }

  function nextStep() {
    if (stepIdx < PHOTO_STEPS.length - 1) setStepIdx(stepIdx + 1);
  }
  function prevStep() {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
  }

  /**
   * Phase 2: process every photo in parallel.
   *   - Decode barcode (native first, ZXing fallback) — fast.
   *   - OCR via /api/prescriptions/ocr — slow (~10–25s each), but parallel.
   * Reads the user's confirmed pharmacy patterns once if a pharmacy name
   * surfaces in any photo so subsequent OCR calls can be smarter.
   */
  async function runProcessing() {
    setPhase("process");
    setBusy(true);
    setBusyMsg(`Reading ${session.photos.length} photo${session.photos.length === 1 ? "" : "s"}…`);

    // First pass: barcode decode (fast, all in parallel)
    let local = session;
    await Promise.all(local.photos.map(async (p) => {
      const bc = await decodeBarcodeDetailedFromFile(p.blob).catch(() => null);
      if (bc) local = setBarcodeResult(local, p.id, bc);
    }));
    setSession(local);

    // Second pass: OCR (slow, in parallel). We don't have a pharmacy hint
    // until we've identified the pharmacy — so run a first OCR pass without
    // hints, then optionally re-run any low-confidence ones with the hint
    // surfaced. For simplicity we just pass an empty hint on the first pass.
    let pharmacyHint = "";
    const ocrResults = await Promise.all(local.photos.map(async (p) => {
      try {
        const ocr = await scanPrescriptionImage(p.blob, pharmacyHint);
        return { id: p.id, ocr };
      } catch (err) {
        const msg = err?.response?.data?.detail || err.message || "OCR failed for one photo.";
        showToast({ variant: "error", message: msg, duration: 3500 });
        return { id: p.id, ocr: null };
      }
    }));
    ocrResults.forEach(({ id, ocr }) => { if (ocr) local = setOcrResult(local, id, ocr); });

    // Look up a learned pharmacy pattern if we now have a pharmacy name.
    const merge = mergeCandidates(local);
    const pharmacy = merge.fields.pharmacy?.value?.trim();
    if (pharmacy) {
      try {
        const pat = await matchPharmacyPattern(pharmacy);
        if (pat) pharmacyHint = `Pharmacy: ${pat.pharmacyName}\nPrior confirmed scans:\n${(pat.examples || []).slice(0, 2).map((ex) => `- ${JSON.stringify(ex)}`).join("\n")}`;
      } catch { /* non-fatal */ }
    }

    // Optional second pass: re-OCR photos whose top-level confidence < 0.5
    // with the pharmacy hint (helps the LLM nail the layout).
    if (pharmacyHint) {
      const weak = local.photos.filter((p) => p.ocr && (Math.max(...Object.values(p.ocr.confidence || { _: 0 })) < 0.5));
      if (weak.length > 0) {
        setBusyMsg(`Refining ${weak.length} photo${weak.length === 1 ? "" : "s"} with learned pharmacy pattern…`);
        const reruns = await Promise.all(weak.map(async (p) => {
          try { return { id: p.id, ocr: await scanPrescriptionImage(p.blob, pharmacyHint) }; }
          catch { return null; }
        }));
        reruns.forEach((r) => { if (r?.ocr) local = setOcrResult(local, r.id, r.ocr); });
      }
    }

    setSession(local);
    setBusy(false);
    setPhase("review");
  }

  function handleConflictChoice(field, value) {
    setSession((s) => applyUserChoice(s, field, value));
  }
  function patchOverride(field, value) {
    setUserOverrides((p) => ({ ...p, [field]: value }));
  }

  async function handleConfirm() {
    const payload = finalConfirmed(session, userOverrides);
    // Fire-and-forget learn step — don't block save.
    if (payload.pharmacy && payload.pharmacy.trim()) {
      learnPharmacyPattern(payload.pharmacy.trim(), {
        medName: payload.medName || "",
        strength: payload.strength || "",
        directions: payload.directions || "",
        rxNumber: payload.rxNumber || "",
        quantity: payload.quantity || "",
        daysSupply: Number(payload.daysSupply) || 0,
        refillsRemaining: Number(payload.refillsRemaining) || 0,
        barcodeFormat: payload.barcodeFormat || "",
      })
        .then(() => showToast({ variant: "success", message: `Learned pattern for "${payload.pharmacy}".`, duration: 3500 }))
        .catch(() => { /* non-fatal */ });
    }
    onConfirm(payload);
  }

  // ---------------------------------------------------------------------------
  // Render — Apple-Sequoia mobile-first sheet
  // ---------------------------------------------------------------------------

  return (
    <div
      data-testid="medication-scan-assistant"
      role="dialog"
      aria-modal="true"
      aria-label="Medication Scan Assistant"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1300,
        display: "flex", alignItems: "stretch", justifyContent: "center",
        background: "rgba(15,23,42,0.50)",
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
      }}
    >
      <div style={{
        background: "var(--surface, #fff)",
        width: "min(640px, 100vw)", height: "min(100vh, 100vh)",
        display: "flex", flexDirection: "column",
        boxShadow: "0 -24px 60px rgba(15,23,42,0.30)",
      }}>
        {/* Header — sticky */}
        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
          <Sparkle size={18} weight="duotone" color="#1F3A6B" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "var(--text-primary)" }}>
              Medication Scan Assistant
            </div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
              {phase === "capture" && `Photo ${stepIdx + 1} of ${PHOTO_STEPS.length} · ${step.title}`}
              {phase === "process" && busyMsg}
              {phase === "review"  && `Confirm extracted fields · ${session.photos.length} photo${session.photos.length === 1 ? "" : "s"}`}
            </div>
          </div>
          <button
            type="button"
            data-testid="msa-close"
            onClick={onCancel}
            aria-label="Close"
            style={{ border: "none", background: "transparent", cursor: "pointer", padding: 8, borderRadius: 8 }}
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Body */}
        {phase === "capture" && (
          <CaptureBody
            step={step}
            stepIdx={stepIdx}
            stepCount={PHOTO_STEPS.length}
            photosForStep={photosForStep}
            allPhotos={session.photos}
            onPick={handlePick}
            onDelete={handleDelete}
          />
        )}
        {phase === "process" && <ProcessBody busy={busy} msg={busyMsg} />}
        {phase === "review" && (
          <ReviewBody
            session={session}
            userOverrides={userOverrides}
            onConflictChoice={handleConflictChoice}
            onPatchOverride={patchOverride}
          />
        )}

        {/* Footer */}
        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border)", background: "var(--surface-2, #F8FAFC)", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {phase === "capture" && (
            <>
              <button type="button" className="btn btn--ghost" onClick={prevStep} disabled={stepIdx === 0} data-testid="msa-prev-step">
                Back
              </button>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: "var(--text-secondary)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <ShieldCheck size={11} weight="bold" /> Photos not stored
              </span>
              {stepIdx < PHOTO_STEPS.length - 1 ? (
                <button type="button" className="btn btn--accent" onClick={nextStep} data-testid="msa-next-step">
                  Next <ArrowRight size={12} weight="bold" />
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn--accent"
                  onClick={runProcessing}
                  disabled={session.photos.length === 0}
                  data-testid="msa-process"
                >
                  <Sparkle size={12} weight="bold" /> READ {session.photos.length} PHOTO{session.photos.length === 1 ? "" : "S"}
                </button>
              )}
            </>
          )}
          {phase === "review" && (
            <>
              <button type="button" className="btn btn--ghost" onClick={() => setPhase("capture")} data-testid="msa-back-to-capture">
                Back to photos
              </button>
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                Review all scanned medication details before saving. Scanner results may be incomplete or incorrect.
              </span>
              <button type="button" className="btn btn--accent" onClick={handleConfirm} data-testid="msa-confirm-all">
                <CheckCircle size={12} weight="bold" /> CONFIRM ALL
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Capture body
// ---------------------------------------------------------------------------

function CaptureBody({ step, stepIdx, stepCount, photosForStep, allPhotos, onPick, onDelete }) {
  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
      <div data-testid="msa-progress" style={{ display: "flex", gap: 4, marginBottom: 14 }}>
        {Array.from({ length: stepCount }).map((_, i) => (
          <div
            key={i}
            data-testid={`msa-progress-dot-${i}`}
            style={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              background: i <= stepIdx ? "#1F3A6B" : "var(--border)",
              transition: "background 200ms ease",
            }}
          />
        ))}
      </div>

      <div style={{ padding: "16px 20px", background: "linear-gradient(135deg, rgba(31,58,107,0.06), rgba(31,58,107,0.02))", border: "1px solid rgba(31,58,107,0.20)", borderRadius: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", color: "#1F3A6B" }} data-testid="msa-step-title">
            STEP {stepIdx + 1}/{stepCount} · {step.title.toUpperCase()}
            {step.optional && <span style={{ marginLeft: 6, fontWeight: 600, opacity: 0.7 }}>(optional)</span>}
          </span>
        </div>
        <div style={{ fontSize: 14, color: "var(--text-primary)", lineHeight: 1.45 }} data-testid="msa-step-hint">
          {step.hint}
        </div>
      </div>

      {photosForStep.length === 0 ? (
        <div style={{ marginTop: 16, padding: 28, border: "2px dashed var(--border)", borderRadius: 16, textAlign: "center", color: "var(--text-secondary)" }}>
          <Camera size={48} weight="duotone" color="#1F3A6B" />
          <div style={{ fontSize: 13, color: "var(--text-primary)", marginTop: 10, marginBottom: 12 }}>
            Tap the button below to use your camera.
          </div>
          <label className="btn btn--accent" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "12px 18px", fontSize: 14, fontWeight: 700 }} data-testid={`msa-capture-${step.id}`}>
            <Camera size={16} weight="bold" /> Take photo
            <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(e) => onPick(e)} />
          </label>
        </div>
      ) : (
        <div style={{ marginTop: 16 }} data-testid="msa-thumbnails">
          {photosForStep.map((p, i) => (
            <PhotoThumbnail
              key={p.id}
              photo={p}
              index={i}
              onDelete={() => onDelete(p.id)}
              onRetake={(e) => onPick(e, p.id)}
              stepLabel={step.title}
            />
          ))}
          <label className="btn btn--outline" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 8 }} data-testid={`msa-add-another-${step.id}`}>
            <Plus size={12} weight="bold" /> Add another photo
            <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(e) => onPick(e)} />
          </label>
        </div>
      )}

      {allPhotos.length > 0 && (
        <div style={{ marginTop: 18, padding: "10px 12px", background: "var(--surface-2, #F8FAFC)", borderRadius: 10, fontSize: 12, color: "var(--text-secondary)" }} data-testid="msa-photo-count-summary">
          <Lightbulb size={12} weight="bold" />{" "}
          {allPhotos.length} photo{allPhotos.length === 1 ? "" : "s"} captured so far across all steps.
        </div>
      )}
    </div>
  );
}

function PhotoThumbnail({ photo, index, onDelete, onRetake, stepLabel }) {
  return (
    <div data-testid={`msa-thumb-${photo.id}`} style={{ display: "flex", gap: 12, alignItems: "center", padding: 10, border: "1px solid var(--border)", borderRadius: 12, marginBottom: 8, background: "var(--surface)" }}>
      <img src={photo.url} alt={`${stepLabel} ${index + 1}`} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border)" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text-primary)" }}>{stepLabel} · #{index + 1}</div>
        <div style={{ fontSize: 11, color: "var(--text-secondary)" }}>{(photo.blob.size / 1024).toFixed(0)} KB</div>
      </div>
      <label className="btn btn--ghost btn--sm" style={{ cursor: "pointer" }} data-testid={`msa-retake-${photo.id}`}>
        <ArrowsClockwise size={11} weight="bold" /> Retake
        <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={onRetake} />
      </label>
      <button type="button" className="btn btn--danger btn--sm" onClick={onDelete} data-testid={`msa-delete-${photo.id}`}>
        <Trash size={11} weight="bold" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Process body
// ---------------------------------------------------------------------------

function ProcessBody({ msg }) {
  return (
    <div style={{ flex: 1, padding: 24, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }} data-testid="msa-process-body">
      <Spinner size={48} weight="bold" className="spin" color="#1F3A6B" />
      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)", textAlign: "center" }}>{msg}</div>
      <div style={{ fontSize: 12, color: "var(--text-secondary)", textAlign: "center", maxWidth: 320, lineHeight: 1.55 }}>
        Reading each photo with vision-OCR + decoding any barcodes. This usually takes 10–25 seconds per photo and runs them in parallel.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Review body — confidence badges + conflict picker + final inputs
// ---------------------------------------------------------------------------

function ReviewBody({ session, userOverrides, onConflictChoice, onPatchOverride }) {
  const merge = useMemo(() => mergeCandidates(session), [session]);
  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 16 }} data-testid="msa-review-body">
      <div style={{ padding: "10px 12px", background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.30)", borderRadius: 10, marginBottom: 12, fontSize: 12, color: "var(--text-primary)", lineHeight: 1.5 }}>
        <Warning size={14} weight="bold" color="#b45309" /> Review all scanned medication details before saving.
        Scanner results may be incomplete or incorrect.
      </div>
      {merge.barcode.rawValue && (
        <div data-testid="msa-review-barcode" style={{ padding: 10, marginBottom: 12, background: "var(--surface-2, #F8FAFC)", border: "1px solid var(--border)", borderRadius: 10 }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.08em", color: "var(--text-secondary)", marginBottom: 4 }}>
            <Barcode size={11} weight="bold" /> BARCODE
            {merge.barcode.format && <span className="mono" style={{ marginLeft: 8, fontWeight: 700, color: "#1F3A6B" }}>{merge.barcode.format.replace(/_/g, " ")}</span>}
            {merge.barcode.source && <span className="mono" style={{ marginLeft: 8, fontSize: 9, color: "var(--text-secondary)" }}>via {merge.barcode.source}</span>}
          </div>
          <div className="mono" style={{ fontSize: 12, color: "var(--text-primary)", wordBreak: "break-all" }}>{merge.barcode.rawValue}</div>
        </div>
      )}

      {/* Conflicts — surface first so the user resolves them */}
      {merge.conflicts.length > 0 && (
        <div data-testid="msa-conflicts" style={{ padding: 12, marginBottom: 12, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.30)", borderRadius: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <WarningCircle size={14} weight="fill" color="#b91c1c" />
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", color: "#b91c1c", textTransform: "uppercase" }}>
              {merge.conflicts.length} conflict{merge.conflicts.length === 1 ? "" : "s"} — choose the correct value
            </span>
          </div>
          {merge.conflicts.map((f) => {
            const cands = merge.fields[f].candidates || [];
            const label = FIELD_ROWS.find((r) => r.id === f)?.label || f;
            return (
              <div key={f} data-testid={`msa-conflict-${f}`} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>{label}:</div>
                <div style={{ display: "grid", gap: 6 }}>
                  {cands.map((c, i) => (
                    <button
                      key={`${f}-cand-${i}`}
                      type="button"
                      onClick={() => onConflictChoice(f, c.value)}
                      data-testid={`msa-conflict-pick-${f}-${i}`}
                      style={{
                        textAlign: "left",
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "1px solid var(--border)",
                        background: "var(--surface)",
                        cursor: "pointer",
                        fontSize: 12.5, color: "var(--text-primary)",
                      }}
                    >
                      <span className="mono" style={{ fontSize: 9.5, padding: "1px 6px", borderRadius: 999, background: "rgba(31,58,107,0.10)", border: "1px solid rgba(31,58,107,0.30)", color: "#1F3A6B", fontWeight: 700, marginRight: 8 }}>
                        Option {i + 1} · {(c.confidence * 100).toFixed(0)}%
                      </span>
                      {c.value}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Field grid */}
      <div style={{ display: "grid", gap: 14 }}>
        {FIELD_ROWS.map((row) => {
          const f = merge.fields[row.id];
          const chip = statusChip(f.status);
          const displayValue = userOverrides[row.id] !== undefined ? userOverrides[row.id] : f.value;
          return (
            <label key={row.id} style={{ display: "block" }} data-testid={`msa-row-${row.id}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: "var(--text-secondary)", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                  {row.label}
                </span>
                {chip && (
                  <span
                    data-testid={`msa-chip-${row.id}`}
                    className="mono"
                    style={{
                      fontSize: 9, fontWeight: 800, letterSpacing: "0.06em",
                      padding: "1px 6px", borderRadius: 999,
                      background: chip.bg, border: `1px solid ${chip.border}`, color: chip.color,
                    }}
                  >
                    {chip.label}
                  </span>
                )}
              </div>
              <input
                className="field-input"
                style={{ fontSize: 16, padding: "10px 12px" }}
                type={row.type || "text"}
                placeholder={row.placeholder}
                value={displayValue || ""}
                onChange={(e) => onPatchOverride(row.id, row.type === "number" ? Number(e.target.value) : e.target.value)}
                data-testid={`msa-input-${row.id}`}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}

// (no unused re-exports)
